<!--
  This dialog will be used to refund delivery order charge.
-->
<template>
  <dialog-form
    class="delivery-order-refund-dialog"
    :show-dialog="isShowDialog"
    @open="dialogOpen"
    @cancel="hideDialog"
    @submit="submitForm"
  >
    <template #title>
      Refund Delivery Order
    </template>
    <div
      v-loading="status.isLoading"
      element-loading-text="Loading Costs..."
      element-loading-custom-class="custom-spinner-class card-spinner-class"
    />
    <div
      v-if="status.isShowRefundSection"
      class="refund-content pb-10"
    >
      <fieldset
        v-loading="status.isRefunding"
        class="customer-cost"
        element-loading-text="Refunding to Customer..."
        element-loading-custom-class="custom-spinner-class"
      >
        <div class="row align-items-center cost-value-section pb-5">
          <label class="col-sm-4 control-label customer-name"> Customer: </label>
          <div class="col-sm-7 pl-0">
            <div class="cost-value customer-name">
              {{ costData?.customerName ?? '' }}
            </div>
          </div>
        </div>

        <div class="row no-margin-width">
          <div class="col-sm-5 cost-value-section pr-0">
            <div class="row align-items-center highlight">
              <label class="col-sm-4 control-label"> Cost: </label>
              <div class="col-sm-4">
                <div class="cost-value">
                  {{ costData?.customerCost ?? '' }}
                </div>
              </div>
              <button
                v-if="isShowPrefillBtnByCost(costData?.customerCost ?? 0)"
                class="btn btn-warning btn-round prefill-action"
                type="button"
                @click="prefillCostField(RefundType.CustomerCost)"
              >
                <i class="fa fa-arrow-right" />
              </button>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-4 control-label"> Refund: </label>
              <div class="col-sm-4">
                <div class="cost-value">
                  {{ costData?.customerRefund ?? '' }}
                </div>
              </div>
            </div>

            <div class="row align-items-center highlight">
              <label class="col-sm-4 control-label"> Credits: </label>
              <div class="col-sm-4">
                <div class="cost-value">
                  {{ costData?.customerCredits ?? '' }}
                </div>
              </div>
              <button
                v-if="isShowPrefillBtnByCost(costData?.customerCredits ?? 0)"
                type="button"
                class="btn btn-warning btn-round prefill-action"
                @click="prefillCostField(RefundType.CustomerCredit)"
              >
                <i class="fa fa-arrow-right" />
              </button>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-6 control-label"> Credits Refund: </label>
              <div class="col-sm-4">
                <div class="cost-value">
                  {{ costData?.customerCreditsRefund ?? '' }}
                </div>
              </div>
            </div>
          </div>

          <div class="col-sm-7 pl-0">
            <form
              method="post"
              class="form-horizontal"
              @submit.prevent="refundChargeCosts()"
            >
              <div class="row align-items-center highlight">
                <div class="col-sm-6">
                  <div class="input-group">
                    <span class="input-group-addon">
                      <b class="fa fa-usd" />
                    </span>
                    <input
                      v-model="refundData.refundAmount"
                      type="number"
                      min="0.5"
                      step=".01"
                      required
                      class="form-control"
                      autocomplete="nope"
                    >
                  </div>
                </div>
                <div class="col-sm-6">
                  <p-button
                    type="purple"
                    round
                    native-type="submit"
                    :disabled="status.isRefunding"
                  >
                    Refund Costs
                  </p-button>
                </div>
              </div>
            </form>

            <div class="row">
              <label class="col-sm-7 control-label">&nbsp;</label>
              <div class="col-sm-4">
                <div class="cost-value" />
              </div>
            </div>

            <form
              method="post"
              class="form-horizontal"
              @submit.prevent="refundChargeCredits()"
            >
              <div class="row align-items-center highlight">
                <div class="col-sm-6">
                  <div class="input-group">
                    <span class="input-group-addon">
                      <b class="fa fa-usd" />
                    </span>
                    <input
                      v-model="refundData.creditsRefundAmount"
                      type="number"
                      min="0"
                      step="any"
                      required
                      class="form-control"
                      autocomplete="nope"
                    >
                  </div>
                </div>
                <div class="col-sm-6">
                  <p-button
                    type="purple"
                    round
                    native-type="submit"
                    :disabled="status.isRefunding"
                  >
                    Refund Credits
                  </p-button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </fieldset>
    </div>
    <template #submit-button>
      Get Cost
    </template>
  </dialog-form>
</template>

<script lang="ts">
import { ref, Ref, reactive } from 'vue';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { RefundType } from '@/models/transaction';
import { CustomChargeTransactionRecord } from '@/models/payment';
import { paymentService } from '@/services/payment';
import { refundService } from '@/services/refund';

const dialogId = 'delivery-order-refund-dialog';

export default {
  name: 'DeliveryOrderRefundDialog',

  props: {
    isShowDialog: {
      type: Boolean,
      default: false,
    },
    transactionId: {
      type: String,
      default: '',
    },
    deliveryOrderId: {
      type: String,
      default: '',
    },
  },

  emits: ['submit', 'cancel'],

  setup: (props: any, context: any) => {
    const { emit } = context;
    const { showErrorPopupDialog, showSuccessPopupDialog, showConfirmPopupDialogWithValidation } = useSwalDialog();

    const status = reactive({
      isLoading: false,
      isShowRefundSection: false,
      isRefunding: false,
    });
    const costData: Ref<CustomChargeTransactionRecord | null> = ref(null);
    const refundData = reactive({
      refundAmount: 0,
      creditsRefundAmount: 0,
    });

    const getCost = async () => {
      status.isLoading = true;
      status.isShowRefundSection = false;
      costData.value = null;
      refundData.refundAmount = 0;
      refundData.creditsRefundAmount = 0;

      try {
        const transaction = await paymentService.getCustomChargeRecord(props.transactionId);

        if (!transaction) {
          showErrorPopupDialog({
            text: `There is no transaction - ${props.transactionId}`,
          });
          return;
        }

        costData.value = {
          id: transaction.id,
          associatedObjectId: transaction.associatedObjectId,
          associatedObjectType: transaction.associatedObjectType,
          customerName: transaction.customerName,
          customerCost: transaction.customerCost,
          customerRefund: transaction.customerRefund,
          customerCredits: transaction.customerCredits,
          customerCreditsRefund: transaction.customerCreditsRefund,
        };

        status.isShowRefundSection = true;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      status.isLoading = false;
    };

    const showRefundNotesConfirmDialog = async (type: string, amount: number) => {
      return showConfirmPopupDialogWithValidation({
        text: `Are you sure you want to refund the ${type} of $${amount}?`,
        title: `Refund ${type}?`,
        input: 'textarea',
        inputPlaceholder: 'Please input refund notes',
        validationText: `Please provide the reason for refunding ${type} of $${amount}`,
        confirmButtonText: 'Yes, Refund',
      });
    };

    const refundCostAsCreditsConfirmDialog = async (amount: number) => {
      return showConfirmPopupDialogWithValidation({
        text: `Refund $${amount} as Credits`,
        title: 'Refund Cost?',
        input: 'checkbox',
        inputPlaceholder: 'Refund Cost as Credits',
        confirmButtonText: 'Yes, Refund as Credits',
      });
    };

    const refundChargeCosts = async () => {
      const refundAmount = refundData.refundAmount;

      const notes = await showRefundNotesConfirmDialog('customer cost', refundAmount);

      if (!notes) {
        return;
      }

      const refundCostAsCredits = await refundCostAsCreditsConfirmDialog(refundAmount);

      status.isRefunding = true;
      try {
        await refundService.refundDeliveryOrder({
          refundCost: refundAmount ? Number(refundAmount) : 0,
          deliveryOrderId: props.deliveryOrderId,
          refundCostAsCredits: !!refundCostAsCredits,
          notes,
        });

        showSuccessPopupDialog({
          text: 'The customer cost has been refunded successfully.',
        });
        refundData.refundAmount = 0;

        await getCost();
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isRefunding = false;
      }
    };

    const refundChargeCredits = async () => {
      const refundAmount = refundData.creditsRefundAmount;

      if (!refundAmount || Number(refundAmount) === 0) {
        showErrorPopupDialog({
          text: 'Refund credit amount must be greater than 0.',
        });
        return;
      }

      const notes = await showRefundNotesConfirmDialog('customer credits', refundAmount);
      if (!notes) {
        return;
      }

      status.isRefunding = true;
      try {
        await refundService.refundDeliveryOrder({
          refundCredits: Number(refundAmount),
          deliveryOrderId: props.deliveryOrderId,
          notes,
        });

        showSuccessPopupDialog({
          text: 'The customer credits has been refunded successfully.',
        });
        refundData.creditsRefundAmount = 0;

        await getCost();
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isRefunding = false;
      }
    };

    const isShowPrefillBtnByCost = (cost: number) => {
      return cost && cost > 0;
    };

    const getPrefillCost = (amount: number) => {
      if (!amount) {
        return 0;
      }

      if (amount >= 0.5) {
        return amount;
      }

      return 0.5; // $0.50 refund minimum
    };

    const prefillCostField = (refundType: RefundType) => {
      if (!costData.value) {
        return;
      }

      if (refundType === RefundType.CustomerCost) {
        refundData.refundAmount = getPrefillCost(costData.value.customerCost ?? 0);
      } else if (refundType === RefundType.CustomerCredit) {
        refundData.creditsRefundAmount = getPrefillCost(costData.value.customerCredits ?? 0);
      }
    };

    const dialogOpen = () => {
      getCost();
    };

    const submitForm = () => {
      getCost();
    };

    const hideDialog = () => {
      emit('cancel');
    };

    return {
      dialogId,
      status,
      util,
      RefundType,
      costData,
      refundData,

      getCost,
      isShowPrefillBtnByCost,
      getPrefillCost,
      prefillCostField,
      refundChargeCosts,
      refundChargeCredits,

      dialogOpen,
      submitForm,
      hideDialog,
    };
  },
};
</script>

<style lang="scss">
.delivery-order-refund-dialog {
  .el-loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .refund-content {
    .row {
      margin-bottom: 0;
    }
    .highlight {
      background: #e1e1e1;
      padding: 2px 0;
    }
    .cost-value-section {
      button.prefill-action {
        min-width: unset;
        font-size: 20px !important;
        padding: 3px 10px !important;
      }
      .customer-name,
      .partner-name {
        font-size: 18px;
        height: fit-content;
        line-height: unset;
        padding-left: 10px;
      }
    }
    .cost-value {
      color: #b33e3e;
      font-size: 16px;
      height: 40px;
      line-height: 40px;
    }
  }
}
</style>
